import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "mt-10" }
const _hoisted_2 = { class: "mt-10" }
const _hoisted_3 = { class: "mt-10" }

import { computed, ref } from "vue";
import { z } from "zod";
import axios from "axios";
import { useRootStore } from "@/store";
import { Alert } from "@/store/classes";
import { ContactUserAuthViewModel, ContactUserSecurityQuestion } from "@/store/classes";
import { useI18n } from "vue-i18n";
import FormField from "@/components/common/FormField.vue3.vue"
import { useFormValidation } from "@/components/common/FormValidation";
import { DropDownValueSchema, PasswordSchema, RequiredStringSchema } from "@/components/common/schemas";


export default /*@__PURE__*/_defineComponent({
  __name: 'InviteForm.vue3',
  props: {
    inviteKey: {},
    securityQuestions: {},
    validationCode: {},
    formTitle: {}
  },
  emits: ['registered'],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const rootStore = useRootStore();
const { t } = useI18n();

const showAlert = (alert: Alert) => rootStore.dispatch('showAlert', alert);
const showLoader = (loaderName: string) => rootStore.dispatch('showLoader', loaderName);
const hideLoader = (loaderName: string) => rootStore.dispatch('hideLoader', loaderName);

const inviteRegisterSchema = z.object({
    username: RequiredStringSchema,
    password: RequiredStringSchema.and(PasswordSchema),
    confirmPassword: RequiredStringSchema,
    securityQuestion1Id: DropDownValueSchema,
    securityQuestion1Answer: RequiredStringSchema,
    securityQuestion2Id: DropDownValueSchema,
    securityQuestion2Answer: RequiredStringSchema,
    securityQuestion3Id: DropDownValueSchema,
    securityQuestion3Answer: RequiredStringSchema,
}).superRefine((val, ctx) => {
    if (val.password !== val.confirmPassword) {
        ctx.addIssue({
            message: t('message.validation.confirmPasswordMatch'),
            code: 'custom',
            path: ['confirmPassword']
        });
    }
});

const savePending = ref(false);
const validationError = ref('');
const invite = ref({
    username: '',
    password: '',
    securityQuestion1Id: undefined,
    securityQuestion1Answer: '',
    securityQuestion2Id: undefined,
    securityQuestion2Answer: '',
    securityQuestion3Id: undefined,
    securityQuestion3Answer: ''
} as ContactUserAuthViewModel);
const confirmPassword = ref('');

const validationState = useFormValidation<z.infer<typeof inviteRegisterSchema>>(
    inviteRegisterSchema,
    computed(() => ({
        ...invite.value,
        confirmPassword: confirmPassword.value,
    })),
    false
);

const securityQuestions1 = computed(() => {
    return props.securityQuestions.filter(question => question.id !== invite.value.securityQuestion2Id && question.id !== invite.value.securityQuestion3Id);
});

const securityQuestions2 = computed(() => {
    return props.securityQuestions.filter(question => question.id !== invite.value.securityQuestion1Id && question.id !== invite.value.securityQuestion3Id);
});

const securityQuestions3 = computed(() => {
    return props.securityQuestions.filter(question => question.id !== invite.value.securityQuestion1Id && question.id !== invite.value.securityQuestion2Id);
});

async function register(): Promise<void> {
    if (savePending.value) return;
    savePending.value = true;
    if (!validationState.validate()) {
        savePending.value = false;
        return;
    }
    try {
        await showLoader('invite');
        validationError.value = '';
        await axios.post(`/auth/invite/${props.inviteKey}/validate`, invite.value);
        await axios.put(`/auth/invite/${props.inviteKey}`, {
            ...invite.value,
            intakeValidationCode: props.validationCode
        });
    } catch (e: any) {
        if (e.response?.status === 400 && e.response?.data)
            validationError.value = e.response.data;
        else
            await showAlert({ text: 'Unable to register, please try again', type: 'error' });
        return;
    } finally {
        await hideLoader('invite');
        savePending.value = false;
    }
    emit('registered');
}

return (_ctx: any,_cache: any) => {
  const _component_p_input_text = _resolveComponent("p-input-text")!
  const _component_p_dropdown = _resolveComponent("p-dropdown")!
  const _component_p_message = _resolveComponent("p-message")!
  const _component_p_button = _resolveComponent("p-button")!
  const _component_p_card = _resolveComponent("p-card")!

  return (_openBlock(), _createBlock(_component_p_card, { class: "form-card" }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_ctx.formTitle), 1)
    ]),
    content: _withCtx(() => [
      _createVNode(FormField, {
        "label-key": "labels.user.username",
        "validation-state": _unref(validationState).result.value.username,
        required: ""
      }, {
        default: _withCtx(({ onBlur }) => [
          _createVNode(_component_p_input_text, {
            modelValue: invite.value.username,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((invite.value.username) = $event)),
            class: "lg:w-5 w-full",
            onBlur: onBlur
          }, null, 8, ["modelValue", "onBlur"])
        ]),
        _: 1
      }, 8, ["validation-state"]),
      _createVNode(FormField, {
        "label-key": "labels.user.password",
        "validation-state": _unref(validationState).result.value.password,
        required: ""
      }, {
        default: _withCtx(({ onBlur }) => [
          _createVNode(_component_p_input_text, {
            modelValue: invite.value.password,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((invite.value.password) = $event)),
            type: "password",
            class: "lg:w-5 w-full",
            onBlur: onBlur
          }, null, 8, ["modelValue", "onBlur"])
        ]),
        _: 1
      }, 8, ["validation-state"]),
      _createVNode(FormField, {
        "label-key": "labels.user.confirmPassword",
        "validation-state": _unref(validationState).result.value.confirmPassword,
        required: ""
      }, {
        default: _withCtx(({ onBlur }) => [
          _createVNode(_component_p_input_text, {
            modelValue: confirmPassword.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((confirmPassword).value = $event)),
            type: "password",
            class: "lg:w-5 w-full",
            onBlur: onBlur
          }, null, 8, ["modelValue", "onBlur"])
        ]),
        _: 1
      }, 8, ["validation-state"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(FormField, {
          "label-key": "labels.user.securityQuestion1",
          "validation-state": _unref(validationState).result.value.securityQuestion1Id,
          required: ""
        }, {
          default: _withCtx(({ onBlur }) => [
            _createVNode(_component_p_dropdown, {
              modelValue: invite.value.securityQuestion1Id,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((invite.value.securityQuestion1Id) = $event)),
              options: securityQuestions1.value,
              "option-label": "securityQuestion",
              "option-value": "id",
              class: "w-full",
              onInput: onBlur
            }, null, 8, ["modelValue", "options", "onInput"])
          ]),
          _: 1
        }, 8, ["validation-state"]),
        _createVNode(FormField, {
          "label-key": "labels.user.securityQuestion1Answer",
          "validation-state": _unref(validationState).result.value.securityQuestion1Answer,
          required: ""
        }, {
          default: _withCtx(({ onBlur }) => [
            _createVNode(_component_p_input_text, {
              modelValue: invite.value.securityQuestion1Answer,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((invite.value.securityQuestion1Answer) = $event)),
              class: "lg:w-5 w-full",
              onBlur: onBlur
            }, null, 8, ["modelValue", "onBlur"])
          ]),
          _: 1
        }, 8, ["validation-state"])
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(FormField, {
          "label-key": "labels.user.securityQuestion2",
          "validation-state": _unref(validationState).result.value.securityQuestion2Id,
          required: ""
        }, {
          default: _withCtx(({ onBlur }) => [
            _createVNode(_component_p_dropdown, {
              modelValue: invite.value.securityQuestion2Id,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((invite.value.securityQuestion2Id) = $event)),
              options: securityQuestions2.value,
              "option-label": "securityQuestion",
              "option-value": "id",
              class: "w-full",
              onInput: onBlur
            }, null, 8, ["modelValue", "options", "onInput"])
          ]),
          _: 1
        }, 8, ["validation-state"]),
        _createVNode(FormField, {
          "label-key": "labels.user.securityQuestion2Answer",
          "validation-state": _unref(validationState).result.value.securityQuestion2Answer,
          required: ""
        }, {
          default: _withCtx(({ onBlur }) => [
            _createVNode(_component_p_input_text, {
              modelValue: invite.value.securityQuestion2Answer,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((invite.value.securityQuestion2Answer) = $event)),
              class: "lg:w-5 w-full",
              onBlur: onBlur
            }, null, 8, ["modelValue", "onBlur"])
          ]),
          _: 1
        }, 8, ["validation-state"])
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(FormField, {
          "label-key": "labels.user.securityQuestion3",
          "validation-state": _unref(validationState).result.value.securityQuestion3Id,
          required: ""
        }, {
          default: _withCtx(({ onBlur }) => [
            _createVNode(_component_p_dropdown, {
              modelValue: invite.value.securityQuestion3Id,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((invite.value.securityQuestion3Id) = $event)),
              options: securityQuestions3.value,
              "option-label": "securityQuestion",
              "option-value": "id",
              class: "w-full",
              onInput: onBlur
            }, null, 8, ["modelValue", "options", "onInput"])
          ]),
          _: 1
        }, 8, ["validation-state"]),
        _createVNode(FormField, {
          "label-key": "labels.user.securityQuestion3Answer",
          "validation-state": _unref(validationState).result.value.securityQuestion3Answer,
          required: ""
        }, {
          default: _withCtx(({ onBlur }) => [
            _createVNode(_component_p_input_text, {
              modelValue: invite.value.securityQuestion3Answer,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((invite.value.securityQuestion3Answer) = $event)),
              class: "lg:w-5 w-full",
              onBlur: onBlur
            }, null, 8, ["modelValue", "onBlur"])
          ]),
          _: 1
        }, 8, ["validation-state"])
      ]),
      (validationError.value)
        ? (_openBlock(), _createBlock(_component_p_message, {
            key: 0,
            severity: "error"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(validationError.value), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_p_button, {
        disabled: savePending.value,
        onClick: register
      }, {
        default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
          _createTextVNode(" Register ", -1)
        ]))]),
        _: 1
      }, 8, ["disabled"])
    ]),
    _: 1
  }))
}
}

})